import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { signUp, type SignUpOutput } from 'aws-amplify/auth'
import { toast } from 'sonner'
import { decodeInviteToken } from '@/auth/utils/decodeInviteToken'
import { useFetchUsagePolicy } from '@/user/userQueries'
import { USAGE_POLICIES } from '@/common/constants'
import { SIX_PENDING_SIGNUP_TERMS_VERSION_ID } from '@/common/storage/keys'
import type { InviteSignupFormValues } from './rules/inviteSignupSchema'

type Result = {
    email: string | null
    isValidToken: boolean
    isRunning: boolean
    signupTerms: ReturnType<typeof useFetchUsagePolicy>['data']
    onSubmit: (data: InviteSignupFormValues) => Promise<SignUpOutput['nextStep'] | undefined>
    onTermsChange: (checked: boolean) => void
}

export function useInviteSignupLogic(): Result {
    const [searchParams] = useSearchParams()
    const [isRunning, setIsRunning] = useState(false)
    const { data: signupTerms } = useFetchUsagePolicy(USAGE_POLICIES.SIGNUP_TERMS)

    // Decode token to extract email from URL
    const decodedToken = useMemo(() => {
        const token = searchParams.get('token')
        if (!token) return null

        try {
            return decodeInviteToken(token)
        } catch {
            return null
        }
    }, [searchParams])

    const persistSignupTermsIntent = useCallback(
        (checked: boolean) => {
            if (checked && signupTerms?.versionId) {
                sessionStorage.setItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID, signupTerms.versionId)
                return
            }

            sessionStorage.removeItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID)
        },
        [signupTerms],
    )

    const onSubmit = useCallback(
        async (data: InviteSignupFormValues) => {
            if (!signupTerms?.versionId) {
                toast.error('Terms are still loading. Please wait a moment and try again.')
                return
            }

            const { email, password } = data
            persistSignupTermsIntent(true)

            try {
                setIsRunning(true)
                const { nextStep } = await signUp({
                    username: email,
                    password,
                    options: {
                        userAttributes: {
                            email,
                        },
                        autoSignIn: true,
                    },
                })
                return nextStep
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : String(error)
                if (errorMessage.includes('already exists')) {
                    toast.error('Email already exists', {
                        id: 'email-exists',
                    })
                    throw error
                }
                toast.error(errorMessage)
                throw error
            } finally {
                setIsRunning(false)
            }
        },
        [signupTerms, persistSignupTermsIntent],
    )

    const onTermsChange = useCallback(
        (checked: boolean) => {
            persistSignupTermsIntent(checked)
        },
        [persistSignupTermsIntent],
    )

    return {
        email: decodedToken?.email ?? null,
        isValidToken: decodedToken !== null,
        isRunning,
        signupTerms,
        onSubmit,
        onTermsChange,
    }
}
